import { sheet } from './day-close'
import { el, todayISO } from './ui'
import { screenLocale } from './round-config'

// The Diary's Move pop-up on a phone (Liam, 22 Oct 2026: "make it easier to move to new day if a job isnt completed we
// have to click the dots"; docs/plans/diary-tidy/PLAN.md v2, the drawings he picked). Six days with how full each is, the
// next working day picked out in lemon, a day nobody works shown faded, and any other date. The move itself is the
// Diary's own (schedule.ts moveToDay: parking hours, gutters, agreement, the sorry message, Undo); this only asks where.

export interface MoveDay { date: string; count: number | null; off: boolean }
export interface MoveSheetOptions {
  first: string // their first name, or "them"
  sub: string // "2:30pm · 18 Barnard Hill"
  days: MoveDay[]
  pick: (date: string) => void
  note?: string
  // The same day next week when the six days do not reach it (Liam's pick 2C, 22 Oct 2026, agreed with Astra: the six
  // days skip it, and it was three taps away in More).
  nextWeek?: string | null
}

const dayWords = (iso: string): string => new Date(`${iso}T12:00:00`).toLocaleDateString(screenLocale(), { weekday: 'short', day: 'numeric' })

// The next working day of the list: the first not marked off (a list with every day off has none picked out).
export function nextWorkingDay(days: MoveDay[]): string | null {
  return days.find((d) => !d.off)?.date ?? null
}

export function openMoveSheet(o: MoveSheetOptions): void {
  const s = sheet(`Move ${o.first}`)
  s.dialog.classList.add('move-sheet')
  s.head.append(el('div', { class: 'move-sheet-title' }, el('h2', {}, `Move ${o.first}`), el('span', { class: 'move-sheet-line', 'aria-hidden': 'true' }), el('p', { class: 'muted' }, o.sub)))
  const next = nextWorkingDay(o.days)
  const grid = el('div', { class: 'move-sheet-days', role: 'group', 'aria-label': 'New day' })
  for (const d of o.days) {
    const sub = d.date === next ? 'next working day' : d.off ? 'day off' : d.count === null ? '' : d.count ? `${d.count} visit${d.count === 1 ? '' : 's'}` : 'free'
    const b = el('button', { type: 'button', class: `move-sheet-day${d.date === next ? ' is-next' : ''}${d.off ? ' is-off' : ''}` },
      el('strong', {}, d.date === todayISO() ? 'Today' : dayWords(d.date)), ...(sub ? [el('small', {}, sub)] : []))
    b.setAttribute('aria-label', `${dayWords(d.date)}${sub ? `, ${sub}` : ''}`)
    b.addEventListener('click', () => { s.close(); o.pick(d.date) })
    grid.append(b)
  }
  const week = o.nextWeek && !o.days.some((d) => d.date === o.nextWeek) && o.nextWeek >= todayISO() ? o.nextWeek : null
  const weekButton = week ? el('button', { type: 'button', class: 'move-sheet-week' }, `Same day next week · ${dayWords(week)}`) : null
  weekButton?.addEventListener('click', () => { s.close(); o.pick(week!) })
  const other = el('input', { type: 'date', class: 'move-sheet-date', min: todayISO(), 'aria-label': 'Another date' }) as HTMLInputElement
  const go = el('button', { type: 'button', class: 'btn-secondary move-sheet-go', disabled: '' }, 'Move')
  other.addEventListener('input', () => {
    const ok = !!other.value && other.value >= todayISO()
    go.toggleAttribute('disabled', !ok)
    go.textContent = ok ? `Move to ${dayWords(other.value)}` : 'Move'
  })
  go.addEventListener('click', () => { if (other.value && other.value >= todayISO()) { s.close(); o.pick(other.value) } })
  s.body.append(grid, ...(weekButton ? [weekButton] : []), el('p', { class: 'muted move-sheet-note' }, o.note ?? 'Keeps the time.'),
    el('div', { class: 'move-sheet-other' }, el('label', {}, 'Pick another date', other), go))
}
